import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';
import Button from './Button';
import { RowTabStopContext } from './row-tab-stop';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export var modalSizes = {
  SMALL: 'w-[400px]',
  SMALL_MEDIUM: 'w-[450px]',
  MEDIUM: 'w-[600px]',
  LARGE: 'w-[800px]'
};

/**
 * A dialog must have a name, or a screen reader says only "dialog" when it
 * opens. `header` renders as the `DialogTitle`, which HeadlessUI wires up as
 * the dialog's `aria-labelledby`. A modal that shows no title names itself
 * through `ariaLabel` instead. TypeScript rejects a call with neither, and a
 * call with both, so a nameless dialog does not compile.
 */

var ModalHeader = function ModalHeader(_ref) {
  var header = _ref.header,
    disableHeaderForcedLowerCase = _ref.disableHeaderForcedLowerCase,
    closeLabel = _ref.closeLabel,
    onCloseClick = _ref.onCloseClick;
  return /*#__PURE__*/_jsxs("header", {
    className: twMerge('flex h-15 shrink-0 items-center px-4', header ? 'justify-between' : 'justify-end'),
    children: [header ? /*#__PURE__*/_jsx(DialogTitle, {
      as: "h1",
      className: twMerge('text-2xl font-bold', disableHeaderForcedLowerCase ? '' : 'lowercase first-letter:uppercase'),
      children: header
    }) : null, /*#__PURE__*/_jsx(Button, {
      iconKey: "close",
      color: "icon",
      "aria-label": closeLabel,
      onClick: onCloseClick,
      "data-test-id": "modal-close-button"
    })]
  });
};
var ModalFooter = function ModalFooter(_ref2) {
  var isMultiRow = _ref2.isMultiRow,
    onRemove = _ref2.onRemove,
    onContinue = _ref2.onContinue,
    onSubmit = _ref2.onSubmit,
    hideCancelButton = _ref2.hideCancelButton,
    confirmButtonColor = _ref2.confirmButtonColor,
    confirmButtonText = _ref2.confirmButtonText,
    disableSubmit = _ref2.disableSubmit,
    cancelLabel = _ref2.cancelLabel,
    removeLabel = _ref2.removeLabel,
    saveLabel = _ref2.saveLabel,
    onClose = _ref2.onClose,
    onRemoveClick = _ref2.onRemoveClick,
    onContinueClick = _ref2.onContinueClick,
    onSubmitClick = _ref2.onSubmitClick;
  // Cancel / Continue / Save share the room Remove leaves, each growing to
  // Button's usual 120px when there is space and shrinking to its text
  // when there is not: Remove + Cancel + Save fit a SMALL dialog on one
  // row instead of overflowing it. `!min-w-max`: Button adds its own
  // min-w after the caller's classes, so a plain min-w-max would be
  // discarded (AC-759). The multi-row footer keeps its full-width Save
  // and 120px Cancel as before.
  var buttonClassName = isMultiRow ? '!min-w-max w-full px-5' : '!min-w-max flex-1 max-w-[120px] px-5';
  var cancelClassName = isMultiRow ? 'px-5' : buttonClassName;
  var resolvedColor = confirmButtonColor !== null && confirmButtonColor !== void 0 ? confirmButtonColor : 'primary';
  var resolvedText = confirmButtonText || saveLabel;
  return /*#__PURE__*/_jsxs("footer", {
    className: twMerge('flex justify-between gap-2 p-6', isMultiRow ? 'flex-col-reverse items-center' : ''),
    children: [/*#__PURE__*/_jsx("div", {
      className: "flex gap-2",
      children: onRemove ? /*#__PURE__*/_jsx(Button, {
        color: "danger",
        className: "self-start",
        onClick: onRemoveClick,
        "data-test-id": "modal-remove-button",
        children: removeLabel
      }) : null
    }), /*#__PURE__*/_jsxs("div", {
      className: twMerge('flex justify-end gap-2', isMultiRow ? 'w-full flex-col-reverse items-center' : 'flex-1 flex-wrap'),
      children: [hideCancelButton ? null : /*#__PURE__*/_jsx(Button, {
        color: "secondary",
        className: cancelClassName,
        onClick: onClose,
        "data-test-id": "modal-cancel-button",
        children: cancelLabel
      }), onContinue ? /*#__PURE__*/_jsx(Button, {
        color: resolvedColor,
        className: buttonClassName,
        onClick: onContinueClick,
        "data-test-id": "modal-continue-button",
        children: resolvedText
      }) : null, onSubmit ? /*#__PURE__*/_jsx(Button, {
        color: resolvedColor,
        className: buttonClassName,
        onClick: onSubmitClick,
        disabled: disableSubmit,
        "data-test-id": "modal-submit-button",
        children: resolvedText
      }) : null]
    })]
  });
};
var BaseModalContent = function BaseModalContent(_ref3) {
  var header = _ref3.header,
    ariaLabel = _ref3.ariaLabel,
    className = _ref3.className,
    open = _ref3.open,
    onContinue = _ref3.onContinue,
    onSubmit = _ref3.onSubmit,
    onClose = _ref3.onClose,
    onRemove = _ref3.onRemove,
    children = _ref3.children,
    confirmButtonText = _ref3.confirmButtonText,
    confirmButtonColor = _ref3.confirmButtonColor,
    disableActions = _ref3.disableActions,
    dataTestId = _ref3.dataTestId,
    disablePadding = _ref3.disablePadding,
    disableSubmit = _ref3.disableSubmit,
    disableHeaderForcedLowerCase = _ref3.disableHeaderForcedLowerCase,
    preventCloseOnSubmit = _ref3.preventCloseOnSubmit,
    _ref3$hideCancelButto = _ref3.hideCancelButton,
    hideCancelButton = _ref3$hideCancelButto === void 0 ? false : _ref3$hideCancelButto,
    _ref3$openConfirmatio = _ref3.openConfirmation,
    openConfirmation = _ref3$openConfirmatio === void 0 ? false : _ref3$openConfirmatio,
    _ref3$closeIconAction = _ref3.closeIconAction,
    closeIconAction = _ref3$closeIconAction === void 0 ? null : _ref3$closeIconAction,
    _ref3$useContactCardS = _ref3.useContactCardStyles,
    useContactCardStyles = _ref3$useContactCardS === void 0 ? false : _ref3$useContactCardS,
    _ref3$modalSize = _ref3.modalSize,
    modalSize = _ref3$modalSize === void 0 ? modalSizes.SMALL : _ref3$modalSize,
    _ref3$isFooterActions = _ref3.isFooterActionsMultiRow,
    isFooterActionsMultiRow = _ref3$isFooterActions === void 0 ? false : _ref3$isFooterActions,
    mainClassName = _ref3.mainClassName;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var _useModal = useModal(),
    previousModal = _useModal.previousModal,
    closeModal = _useModal.closeModal;
  var handleClose = function handleClose() {
    onClose === null || onClose === void 0 || onClose();
    previousModal === null || previousModal === void 0 || previousModal();
  };
  var handleContinue = function handleContinue() {
    onContinue();
  };
  var handleRemove = function handleRemove() {
    onRemove();
    !openConfirmation && closeModal();
  };
  var handleSubmit = function handleSubmit() {
    onSubmit();
    if (preventCloseOnSubmit) return;
    closeModal === null || closeModal === void 0 || closeModal();
  };
  var _useSinglePaneMode = useSinglePaneMode(),
    isSinglePaneMode = _useSinglePaneMode.isSinglePaneMode;
  return /*#__PURE__*/_jsx(Transition, {
    show: open,
    as: Fragment,
    children: /*#__PURE__*/_jsxs(Dialog, {
      "static": true,
      as: "div",
      className: "relative z-20",
      "aria-label": ariaLabel,
      onClose: handleClose,
      "data-test-id": dataTestId,
      children: [/*#__PURE__*/_jsx(TransitionChild, {
        as: Fragment,
        enter: "ease-out duration-300",
        enterFrom: "opacity-0",
        enterTo: "opacity-100",
        leave: "ease-in duration-200",
        leaveFrom: "opacity-100",
        leaveTo: "opacity-0",
        children: /*#__PURE__*/_jsx("div", {
          className: "fixed inset-0 bg-gray-900 opacity-70 transition-opacity dark:bg-gray-100"
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "fixed inset-0 z-30 w-screen overflow-y-auto",
        children: /*#__PURE__*/_jsx("div", {
          className: "flex min-h-full items-center justify-center p-4 text-center",
          children: /*#__PURE__*/_jsx(TransitionChild, {
            as: Fragment,
            enter: "ease-out duration-300",
            enterFrom: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
            enterTo: "opacity-100 translate-y-0 sm:scale-100",
            leave: "ease-in duration-200",
            leaveFrom: "opacity-100 translate-y-0 sm:scale-100",
            leaveTo: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
            children: /*#__PURE__*/_jsxs(DialogPanel, {
              className: twMerge(isSinglePaneMode ? 'fixed !size-full' : "relative flex max-h-[80dvh] ".concat(modalSize), 'shadow-base transform flex-col overflow-hidden rounded-sm bg-white text-start transition-all', useContactCardStyles ? 'dark:bg-white' : 'dark:bg-gray-200', className),
              "data-test-id": "modal-dialog",
              children: [/*#__PURE__*/_jsx(ModalHeader, {
                header: header,
                disableHeaderForcedLowerCase: disableHeaderForcedLowerCase,
                closeLabel: i18next.t('close'),
                onCloseClick: closeIconAction || handleClose
              }), /*#__PURE__*/_jsx("main", {
                className: twMerge(
                // py-1 leaves room for the focus
                // ring of a control at the top or the
                // bottom, which the scroller cuts off.
                'scrollbar h-full flex-1 overflow-y-scroll py-1', disableActions ? 'max-h-[calc(100%-60px)]' : 'max-h-[calc(100%-140px)]', disablePadding ? '' : 'px-4', mainClassName),
                children: children
              }), disableActions ? null : /*#__PURE__*/_jsx(ModalFooter, {
                isMultiRow: isFooterActionsMultiRow,
                onRemove: onRemove,
                onContinue: onContinue,
                onSubmit: onSubmit,
                hideCancelButton: hideCancelButton,
                confirmButtonColor: confirmButtonColor,
                confirmButtonText: confirmButtonText,
                disableSubmit: disableSubmit,
                cancelLabel: i18next.t('cancel'),
                removeLabel: i18next.t('remove'),
                saveLabel: i18next.t('navigate.save'),
                onClose: handleClose,
                onRemoveClick: handleRemove,
                onContinueClick: handleContinue,
                onSubmitClick: handleSubmit
              })]
            })
          })
        })
      })]
    })
  });
};

// Context passes through portals, so a dialog that a list row opens would
// otherwise take the row's Tab stop state.
var BaseModal = function BaseModal(props) {
  return /*#__PURE__*/_jsx(RowTabStopContext.Provider, {
    value: undefined,
    children: /*#__PURE__*/_jsx(BaseModalContent, _objectSpread({}, props))
  });
};
export default BaseModal;